import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import FingerPrintJS from '@fingerprintjs/fingerprintjs';
import { Button } from './ui/button';
import { Comment } from '../types/Comment.type';
import { formatTimeAgo } from '../util/DateUtil';
import {
  CreateCommentRequest,
  CreateCommentMutationVariables,
  CreateCommentResponse,
  GetCommentsResponse,
  CommentLikeToggleRequest,
} from '../types/Comment.api.type';
import { commentInitializer } from '../types/Comment.initializer';
import { commentApi } from '../api/comment.api';
import { ChevronDownIcon, EditIcon, FlagIcon, HeartIcon, MoreHorizontalIcon, TrashIcon } from './ui/icons';
import { User } from '../types/User.type';
import { useOnClickOutside } from '../hooks/useOnClickOutside';

interface CommentSectionProps {
  questionId: string;
  isAuthenticated: boolean;
  user?: User;
}

const UserAvatar = ({ size = 'md', name = '익명' }: { size?: 'sm' | 'md' | 'lg'; name?: string }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-7 h-7 text-xs',
    lg: 'w-8 h-8 text-sm',
  };

  // 이름 기반으로 색상 생성 (단색)
  const getAvatarColor = (aname: string) => {
    const colors = [
      'bg-slate-500',
      'bg-gray-500',
      'bg-zinc-500',
      'bg-neutral-500',
      'bg-stone-500',
      'bg-red-500',
      'bg-blue-500',
      'bg-green-500',
    ];
    const index = aname.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div
      className={`${sizeClasses[size]} ${getAvatarColor(name)} rounded-full flex items-center justify-center text-white font-medium flex-shrink-0`}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

/**
 * 설문 댓글 파트
 *
 * @author hjkim
 * @constructor
 */
const CommentSection = ({ questionId, isAuthenticated, user }: CommentSectionProps) => {
  /**
   * Hooks
   */
  /* query client */
  const queryClient = useQueryClient();
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  /**
   * Variables
   */
  /* 댓글 최대 입력 글자수*/
  const COMMENT_MAX_LENGTH = 200;
  /* 댓글 최소 입력 글자수 (부실 댓글 방지) */
  const MIN_COMMENT_LENGTH = 3;

  /**
   * States
   */
  /* 댓글 오픈 여부 state */
  const [isOpenComment, setIsOpenComment] = useState<boolean>(false);
  /* 댓글 작성 파라미터 state */
  const [commentCreateParam, setCommentCreateParam] = useState<CreateCommentRequest>(
    commentInitializer.INITIAL_CREATE_COMMENT_PARAM,
  );
  /* api 조회 후 받아온 댓글 데이터 state */
  // const [commentList, setCommentList] = useState<GetCommentsResponse[]>(
  //   commentInitializer.INITIAL_COMMENT_LIST,
  // );
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [editingComment] = useState<string | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState<string | null>(null);
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  /**
   * APIs
   */
  /* 댓글 및 대댓글 작성 API */
  const { mutate: createCommentMutation } = useMutation<CreateCommentResponse, Error, CreateCommentMutationVariables>({
    mutationFn: (param: CreateCommentMutationVariables) =>
      commentApi.createComment(param).then((res) => res.data.results),
    // 이 뮤테이션은 전역 로딩 스피너를 표시하지 않도록 meta 태그 추가
    meta: { hideGlobalLoader: true },
    onSuccess: (newComment) => {
      // 성공시, 토스트 메시지
      toast.success('댓글등록이 성공했습니다.');
      // 캐시를 직접 업데이트하여 불필요한 API 호출 방지
      queryClient.setQueryData<Comment[]>(['comments', questionId], (oldData = []) => {
        if (!newComment || typeof newComment !== 'object') {
          return oldData;
        }
        // 최상위 댓글 추가 (대댓글 기능 제거됨)
        const newCommentForCache: Comment = {
          ...newComment,
          isAnonymous: !!newComment.fingerprint,
          profileImageUrl: newComment.profileImageUrl,
          replies: [],
          likeCount: 0,
          liked: false,
        };
        return [...oldData, newCommentForCache];
      });

      // 메인 댓글 폼 초기화
      setCommentCreateParam(commentInitializer.INITIAL_CREATE_COMMENT_PARAM);
    },
    onError: (error) => {
      // TODO: 사용자에게 에러 피드백을 제공하는 로직 (e.g., toast message)
      console.error('댓글 작성에 실패했습니다:', error);
    },
  });
  /**
   * 댓글 및 대댓글 조회 api
   *
   * - 기존에는 설문 상세정보를 조회할 때, 댓글정보도 가져왔으나, 댓글 등록후 재조회 문제로 나누어 api구현함.
   */
  const { data: commentData } = useQuery({
    queryKey: ['comments', questionId],
    queryFn: () => commentApi.getComments(questionId, fingerprint).then((res) => res.data.results),
    initialData: commentInitializer.INITIAL_COMMENT_LIST,
    enabled: !!questionId && (isAuthenticated || !!fingerprint),
  });

  // 댓글 좋아요 토글
  const { mutate: toggleCommentLike } = useMutation({
    mutationFn: (params: CommentLikeToggleRequest) => commentApi.toggleLike(params),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['comments', questionId] });
      const prev = queryClient.getQueryData<GetCommentsResponse[]>(['comments', questionId]);
      if (prev) {
        queryClient.setQueryData<GetCommentsResponse[]>(
          ['comments', questionId],
          prev.map((c) => {
            if (String(c.id) === String(params.commentId)) {
              return { ...c, liked: !c.liked, likeCount: c.liked ? c.likeCount - 1 : c.likeCount + 1 };
            }
            return {
              ...c,
              replies: c.replies.map((r) => {
                if (String(r.id) === String(params.commentId)) {
                  return { ...r, liked: !r.liked, likeCount: r.liked ? r.likeCount - 1 : r.likeCount + 1 };
                }
                return r;
              }),
            };
          }),
        );
      }
      return { prev };
    },
    onError: (_err, _params, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['comments', questionId], context.prev);
      }
    },
  });

  const handleToggleLike = (commentId: string) => {
    toggleCommentLike({
      commentId: Number(commentId),
      fingerprint: !isAuthenticated && fingerprint ? fingerprint : undefined,
    });
  };

  /**
   * Handlers
   */
  /* 댓글 작성 파라미터 세팅 handler */
  const setCommentParamHandler = async (key: keyof CreateCommentRequest, value: string) => {
    console.log('key', key, 'value', value);
    setCommentCreateParam((current) => ({
      ...current,
      [key]: value,
    }));
  };
  /* 댓글 작성 API 요청 handler */
  const handleCreateCommentHandler = async () => {
    // Validation: 3자 이상 ~ 최대 글자수 이내 (부실 댓글 방지)
    const content = commentCreateParam.commentContent?.trim() || '';
    if (content.length < MIN_COMMENT_LENGTH) {
      return;
    }
    if (content.length > COMMENT_MAX_LENGTH) {
      return;
    }
    try {
      const param = {
        ...commentCreateParam,
        questionId,
        commentContent: commentCreateParam.commentContent || '',
      };

      // 익명 사용자인 경우, fingerprint를 추가
      if (!isAuthenticated) {
        const fp = await FingerPrintJS.load();
        const result = await fp.get();
        param.fingerprint = result.visitorId;
      }

      createCommentMutation(param);
    } catch (error) {
      console.error('댓글 작성 중 오류:', error);
    }
  };
  /* */
  const handleMoreMenu = (commnetId: string) => {
    setShowMoreMenu(showMoreMenu === commnetId ? null : commnetId);
  };
  /* 댓글 삭제 handler */
  const handleDeleteReply = (commentId: string) => {
    console.log('delete', commentId);
  };
  /* 유저 정보 페이지이동 handler */
  const navigateToUserInfo = (userId: string) => {
    console.log('navigateToUserInfo', userId);
    navigate(`/user/info/${userId}`);
  };
  useOnClickOutside(moreMenuRef, () => setShowMoreMenu(null));

  // 비회원인 경우 fingerprint 초기화
  useEffect(() => {
    if (!isAuthenticated) {
      FingerPrintJS.load()
        .then((fp) => fp.get())
        .then((result) => setFingerprint(result.visitorId));
    }
  }, [isAuthenticated]);

  return (
    <div className="mt-6">
      {/* 댓글 해더 */}
      <div className="flex items-center justify-between py-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <h3 className="font-semibold text-gray-900">댓글 {commentData.length}개</h3>
        </div>
        <button
          type="button"
          onClick={() => setIsOpenComment(!isOpenComment)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-70"
        >
          <ChevronDownIcon className={`w-4 h-4 chevron-transition ${isOpenComment ? 'rotate-180' : ''}`} />
          {isOpenComment ? '접기' : '펼치기'}
        </button>
      </div>
      {/* 댓글 내용 */}
      <div
        className={`comment-section-transition ${isOpenComment ? 'max-h-[2000px] opacity-100 animate-slide-down' : 'max-h-0 opacity-0'}`}
        style={{ maxHeight: isOpenComment ? '2000px' : '0px', opacity: isOpenComment ? 1 : 0 }}
      >
        <div className="pt-4">
          {/* 댓글 작성 폼 */}
          <div className="mb-6">
            <div className="flex gap-3">
              <div className="flex-1">
                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleCreateCommentHandler();
                  }}
                >
                  <textarea
                    placeholder="3자 이상 입력해주세요."
                    className="w-full p-3 border border-gray-200 rounded-lg resize-none text-sm focus:outline-none focus:border-gray-400 transition-colors"
                    rows={focusedInput === 'main' ? 4 : 1}
                    value={commentCreateParam.commentContent}
                    onFocus={() => setFocusedInput('main')}
                    onBlur={() => {
                      if (!commentCreateParam.commentContent.trim()) {
                        setFocusedInput(null);
                      }
                    }}
                    onChange={(e) => setCommentParamHandler('commentContent', e.target.value)}
                  />
                  <div className="pl-4 flex items-center justify-between">
                    {commentCreateParam.commentContent.length > 200 ? (
                      <span className="text-xs text-red-600 font-bold">댓글 내용은 200자를 초과할 수 없습니다.</span>
                    ) : (
                      <span />
                    )}
                    <div className="text-right text-xs text-muted-foreground">
                      <span
                        className={commentCreateParam.commentContent.length > COMMENT_MAX_LENGTH ? 'text-red-600' : ''}
                      >
                        {commentCreateParam.commentContent.length}
                      </span>
                      /{COMMENT_MAX_LENGTH}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    {isAuthenticated ? (
                      <span className="" />
                    ) : (
                      <span className="pl-4 text-xs text-gray-400">익명으로 작성됩니다.</span>
                    )}
                    <Button
                      type="submit"
                      className="text-sm"
                      disabled={commentCreateParam.commentContent.trim().length < MIN_COMMENT_LENGTH}
                    >
                      댓글 작성
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          {/* 댓글 목록 */}
          {commentData.length > 0 ? (
            <div className="space-y-6">
              {commentData.map((comment) => (
                <div key={comment.id}>
                  {/* 메인 댓글 */}
                  <div className="flex gap-3 group comment-hover">
                    {comment.profileImageUrl ? (
                      <img src={comment.profileImageUrl} alt="profile" className="w-7 h-7 rounded-full" />
                    ) : (
                      <UserAvatar size="md" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-gray-900 text-sm">
                          <button type="button" onClick={() => navigateToUserInfo('14')}>
                            {comment.authorName}
                          </button>
                        </span>
                        <span className="text-xs text-gray-500">{formatTimeAgo(comment.regDateTime)}</span>
                      </div>
                      {/* 댓글 내용 또는 수정 폼 */}
                      {editingComment === comment.id ? (
                        <div className="mb-3">
                          <textarea
                            rows={2}
                            className="w-full border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-gray-400"
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              className="px-2 py-1 text-xs bg-black text-white rounded hover:bg-gray-800 transition-colors"
                            >
                              저장
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-900 transition-colors"
                            >
                              취소
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <p className="text-gray-800 leading-relaxed break-words text-sm">{comment.commentContent}</p>
                        </div>
                      )}
                      {/* 댓글 액션 버튼 - 좋아요 */}
                      {editingComment !== comment.id && (
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => handleToggleLike(comment.id)}
                            className={`flex items-center gap-1 text-xs transition-colors ${comment.liked ? 'text-red-500' : 'text-gray-300 hover:text-gray-400'}`}
                          >
                            <HeartIcon className="w-3.5 h-3.5" />
                            {comment.likeCount > 0 && (
                              <span className={comment.liked ? 'text-red-500' : 'text-gray-500'}>
                                {comment.likeCount}
                              </span>
                            )}
                          </button>
                          {/* 싫어요 (추후 적용) */}
                          {/* <button
                            type="button"
                            className={`flex items-center gap-1 text-xs transition-colors text-gray-500 hover:text-gray-700`}>
                            <ThumbsDownIcon className="w-3 h-3" />
                            싫어요
                          </button> */}
                          {/* 더보기 버튼 */}
                          <div className="relative ml-auto">
                            <button
                              type="button"
                              onClick={() => handleMoreMenu(comment.id)}
                              className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <MoreHorizontalIcon className="w-4 h-4" />
                            </button>
                            {showMoreMenu === comment.id && (
                              <div
                                ref={moreMenuRef}
                                className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden"
                              >
                                <button
                                  type="button"
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors text-gray-700"
                                >
                                  <EditIcon className="w-3 h-3" />
                                  수정
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteReply(comment.id)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors text-gray-700"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                  삭제
                                </button>
                                <button
                                  type="button"
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors text-gray-700"
                                >
                                  <FlagIcon className="w-3 h-3" />
                                  신고
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-6 border-b border-gray-100" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm">댓글이 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentSection;
